import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  AutoAwesome,
  BarChartOutlined,
  CloseRounded,
  ChevronRightRounded,
  HomeOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  LogoutRounded,
  SettingsOutlined,
  StorefrontOutlined,
  SupportAgentOutlined,
  AttachMoneyOutlined,
  ReplayOutlined,
} from '@mui/icons-material';
import { logout } from '../store/actions/authActions';
import { bazaarsEnabled } from '../config/featureFlags';

const ICONS = {
  dashboard: HomeOutlined,
  inventory: Inventory2Outlined,
  orders: LocalShippingOutlined,
  finance: AttachMoneyOutlined,
  settings: SettingsOutlined,
  support: SupportAgentOutlined,
  pulse: AutoAwesome,
  bazaars: StorefrontOutlined,
  backtrack: ReplayOutlined,
  confirmit: BarChartOutlined,
};

function Icon({ name }) {
  const Cmp = ICONS[name] || HomeOutlined;
  return <Cmp sx={{ fontSize: 20 }} />;
}

function isPathActive(pathname, to) {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function NavItem({ item, pathname }) {
  const [hover, setHover] = useState(false);
  const active = isPathActive(pathname, item.to);
  return (
    <NavLink to={item.to} end={item.to === '/'} style={{ textDecoration: 'none' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 13,
          borderRadius: 13,
          cursor: 'pointer',
          color: active ? 'var(--red-tx)' : 'var(--mut)',
          background: !active && hover ? 'var(--s06)' : 'transparent',
          transition: 'background .18s ease, color .18s ease',
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 13,
            display: 'grid',
            placeItems: 'center',
            background: active ? 'linear-gradient(160deg, #9c0410, #4a0107)' : 'transparent',
            color: active ? '#ffeaea' : 'inherit',
            boxShadow: active ? '0 8px 24px rgba(125,1,12,0.60)' : 'none',
          }}
        >
          <Icon name={item.icon} />
        </div>
        <span className="cz-label" style={{ fontSize: 13.5, fontWeight: active ? 600 : 400, color: active ? 'var(--red-tx)' : 'var(--tx2)' }}>
          {item.label}
        </span>
      </div>
    </NavLink>
  );
}

function MobileNavItem({ item, onClose, pathname }) {
  const active = isPathActive(pathname, item.to);
  return (
    <NavLink to={item.to} end={item.to === '/'} onClick={onClose} style={{ textDecoration: 'none' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 13,
          padding: '8px 10px',
          borderRadius: 14,
          background: active ? 'linear-gradient(160deg, rgba(125,1,12,0.32), rgba(74,1,7,0.20))' : 'transparent',
          color: active ? 'var(--red-tx)' : 'var(--tx2)',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 12,
            display: 'grid',
            placeItems: 'center',
            background: active ? 'linear-gradient(160deg, #9c0410, #4a0107)' : 'transparent',
            color: active ? '#ffeaea' : 'inherit',
            boxShadow: active ? '0 8px 24px rgba(125,1,12,0.55)' : 'none',
          }}
        >
          <Icon name={item.icon} />
        </div>
        <span style={{ fontSize: 14.5, fontWeight: active ? 600 : 500 }}>{item.label}</span>
      </div>
    </NavLink>
  );
}

function SidebarInner({ isMobile = false, open = false, onClose, pathname, user, logout }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [railOpen, setRailOpen] = useState(false);

  const primary = [
    { to: '/', label: t('navigation.dashboard'), icon: 'dashboard' },
    { to: '/inventory', label: t('navigation.inventory'), icon: 'inventory' },
    { to: '/orders', label: t('navigation.orders'), icon: 'orders' },
    { to: '/finance', label: t('navigation.finance'), icon: 'finance' },
    { to: '/settings', label: t('navigation.settings'), icon: 'settings' },
    { to: '/support', label: t('navigation.support'), icon: 'support' },
  ];
  const extra = [
    { to: '/pulse', label: t('navigation.chainzPulse'), icon: 'pulse' },
    bazaarsEnabled ? { to: '/bazaars', label: t('navigation.bazaars'), icon: 'bazaars' } : null,
    { to: '/returns-system', label: t('navigation.backtrack'), icon: 'backtrack' },
    user?.vendor?.confirmItUUID ? { to: '/confirmit-dashboard', label: 'ConfirmIt', icon: 'confirmit' } : null,
  ].filter(Boolean);

  const items = [...primary, ...extra];

  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 80,
            background: 'rgba(0,0,0,0.55)',
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transition: 'opacity .25s ease',
          }}
        />
        <div
          style={{
            position: 'fixed',
            top: 0,
            [isRTL ? 'right' : 'left']: 0,
            bottom: 0,
            zIndex: 90,
            width: 252,
            maxWidth: '82vw',
            padding: '18px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            background: 'var(--side-bg)',
            [isRTL ? 'borderLeft' : 'borderRight']: '1px solid var(--s07)',
            boxShadow: '0 18px 60px var(--sh50)',
            transform: open ? 'translateX(0)' : `translateX(${isRTL ? '100%' : '-100%'})`,
            transition: 'transform .28s cubic-bezier(.22,.8,.3,1)',
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '2px 6px 14px' }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, background: 'linear-gradient(160deg, var(--s10), var(--s02))', border: '1px solid var(--s10)', display: 'grid', placeItems: 'center' }}>
              <img src={`${process.env.PUBLIC_URL || ''}/chainz-logo.png`} alt="Chainz" style={{ width: 30, height: 'auto', display: 'block' }} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>Chainz</div>
            <div
              onClick={onClose}
              style={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'linear-gradient(160deg, var(--s10), var(--s03))', border: '1px solid var(--s12)', color: 'var(--tx2)', cursor: 'pointer' }}
              aria-label="Close menu"
            >
              <CloseRounded sx={{ fontSize: 18 }} />
            </div>
          </div>
          {items.map((item) => (
            <MobileNavItem key={item.to} item={item} onClose={onClose} pathname={pathname} />
          ))}
          <div
            onClick={() => { logout(); onClose(); }}
            style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 13, padding: '8px 10px', borderRadius: 14, cursor: 'pointer', color: 'var(--red-tx2)' }}
          >
            <div style={{ width: 40, height: 40, display: 'grid', placeItems: 'center' }}>
              <LogoutRounded sx={{ fontSize: 20 }} />
            </div>
            <span style={{ fontSize: 14.5, fontWeight: 500 }}>{t('navigation.logout')}</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, paddingTop: 6, position: 'sticky', top: 28, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 56px)' }}>
      <div style={{ width: 52, height: 52, borderRadius: 16, background: 'linear-gradient(160deg, var(--s10), var(--s02))', border: '1px solid var(--s10)', display: 'grid', placeItems: 'center', boxShadow: '0 10px 30px var(--sh45)' }}>
        <img src={`${process.env.PUBLIC_URL || ''}/chainz-logo.png`} alt="Chainz" style={{ width: 34, height: 'auto', display: 'block' }} />
      </div>
      <div
        className={`cz-toggle ${railOpen ? 'open' : ''}`}
        onClick={() => setRailOpen((o) => !o)}
        style={{ width: 32, height: 32, marginTop: -8, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'linear-gradient(160deg, var(--s10), var(--s03))', border: '1px solid var(--s12)', color: 'var(--tx2)', cursor: 'pointer' }}
      >
        <ChevronRightRounded sx={{ fontSize: 18 }} />
      </div>
      <div
        className={`cz-side ${railOpen ? 'open' : ''}`}
        style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 10px', borderRadius: 20, background: 'var(--side-bg)', border: '1px solid var(--s07)', overflow: 'hidden', overflowY: 'auto', boxShadow: '0 18px 44px var(--sh50)', maxHeight: 'calc(100vh - 180px)' }}
      >
        {primary.map((item) => (
          <NavItem key={item.to} item={item} pathname={pathname} />
        ))}
        <div style={{ height: 1, background: 'var(--s10)', margin: '4px 8px' }} />
        {extra.map((item) => (
          <NavItem key={item.to} item={item} pathname={pathname} />
        ))}
        <div style={{ height: 1, background: 'var(--s10)', margin: '4px 8px' }} />
        <div
          role="button"
          tabIndex={0}
          onClick={() => logout()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') logout(); }}
          title={t('navigation.logout')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 13,
            borderRadius: 13,
            cursor: 'pointer',
            color: 'var(--red-tx2)',
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 13,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <LogoutRounded sx={{ fontSize: 20 }} />
          </div>
          <span className="cz-label" style={{ fontSize: 13.5, fontWeight: 500 }}>
            {t('navigation.logout')}
          </span>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({ user: state?.auth?.user });
export default connect(mapStateToProps, { logout })(SidebarInner);
